import {
  DateISOString,
  DateString,
  ListFilters,
  PaginatedList,
  ResponseError,
} from '@/lib/core/interfaces/common.types'
import { Organization } from '@/lib/core/interfaces/organization.types'
import { User, UserId } from '@/lib/core/interfaces/user.types'

import { Colleague } from '@/lib/features/Account/interfaces'
import {
  Asset,
  AssetContentType,
  AssetId,
  BridgeId,
  BridgeStatus,
} from '@/lib/features/Assets/interfaces'

export type ShareId = number

export type Share = {
  assetId: AssetId
  reference: string
  bridgeId: BridgeId
  bridgeStatus: BridgeStatus
  canToggleMap: boolean
  contactTo?: User
  contactFrom?: User
  contentType: AssetContentType
  dateFrom: DateString
  dateTo: DateString
  description: Asset['description']
  id: ShareId
  isActive: boolean
  issuedOn: DateString
  make: Asset['make']
  model: Asset['model']
  organization: Organization
  plateNumber: Asset['plateNumber']
  seenAtByOrganizationTo: DateISOString | null
  shareClass?: ShareClass
}

export type ActiveSharesPayload = ListFilters

export type AssetActiveSharesPayload = {
  assetId: number
  pageSize?: number
}

export type ActiveSharesResponse = PaginatedList<Share> & {
  countUnseen: number
  countLent: number
  countBorrowed: number
  countAll: number
  countOnward: number
}
export type ActiveSharesResponseError = ResponseError

export type CancelActiveSharesPayload = ShareId
export type CancelActiveSharesResponse = void
export type CancelActiveSharesResponseError = ResponseError

export type ExtendActiveShareId = ShareId
export type HistorySharesPayload = ListFilters
export type HistorySharesResponse = PaginatedList<Share> & {
  countUnseen: number
  countLent: number
  countBorrowed: number
  countAll: number
  countOnward: number
}
export type HistorySharesResponseError = ResponseError

export type SharesSharedFromMePayload = ListFilters
export type SharesSharedFromMeResponse = PaginatedList<Share>
export type SharesSharedFromMeResponseError = ResponseError

export type SharesSharedToMePayload = ListFilters
export type SharesSharedToMeResponse = PaginatedList<Share>
export type SharesSharedToMeResponseError = ResponseError

export type SinglePostSharesPayload = {
  asset: AssetId
  dateFrom: DateString
  dateTo: DateString
  contactTo: UserId | null | undefined
  contactFrom: UserId | null | undefined
  organizationTo: Organization['id']
  reference: string
  parentShare: ShareId | null | undefined
  shareClass?: SelectableShareClass
}

export type SinglePatchSharesPayload = {
  id: ExtendActiveShareId
  share: SinglePostSharesPayload
}

export type SinglePostSharesResponse = {
  asset: Asset
  cost: string
  currency: string
  dateFrom: DateString
  dateTo: DateString
  contactTo: Colleague
  ContactFrom: Colleague
  issuedOn: DateISOString
  organizationTo: Organization
  reference: string
  shareClass?: ShareClass
}
export type PostSharesPayload = Array<SinglePostSharesPayload>
export type PostSharesResponse = Array<SinglePostSharesResponse>
export type PostSharesResponseError = {
  _error?: Array<unknown>
  [key: number]: {
    [key in keyof SinglePostSharesPayload]?: Array<string>
  }
}
export type PostSharesValidationResponseError = {
  _error?: Array<unknown>
  [key: number]: {
    warning: string
  }
}
export type PatchSharePayload = SinglePatchSharesPayload

/**
 * The share PATCH endpoint does not treat the body as a partial update — it re-validates the whole
 * share, so omitting `dateFrom`/`dateTo` fails with "Invalid date format for 'date_from': 'None'".
 * A reference edit therefore echoes the share's current values back verbatim and overrides only the
 * reference, leaving the share period, asset, organisation and contacts untouched.
 */
export type ShareReferencePatchData = {
  asset: AssetId
  dateFrom: DateString
  dateTo: DateString
  organizationTo: Organization['id']
  contactTo: UserId | null
  contactFrom: UserId | null
  reference: string
}

export type UpdateShareReferencePayload = {
  id: ShareId
  data: ShareReferencePatchData
}
export type UpdateShareReferenceResponse = {
  id: ShareId
  reference: string
}
export type UpdateShareReferenceResponseError = ResponseError

export type UpdateMapAccessPayload = {
  id: ShareId
  data: { isActive: boolean }
}
export type UpdateMapAccessResponse = {
  id: ShareId
  isActive: boolean
}
export type UpdateMapAccessResponseError = ResponseError

export type PostShareValidatePayload = {
  asset: number
  dateFrom: DateString
  dateTo: DateString
  organizationTo: number
  shareId?: number
  acceptWarning: boolean
  shareClass?: SelectableShareClass
}

export type ShareValidationResponse = {
  isValid: boolean
}

/**
 * Mirrors the backend `ShareClass` choices (`smart_share/choices.py`).
 *
 * - `EXPLICIT` is a standard Smart Share: the asset is visible on the contact's map and asset list.
 * - `DELEGATION` cedes the asset for use in the contact's tasks without granting any location
 *   visibility. Visibility only follows from the task machinery once a task starts (ADR-0005).
 * - `TASK_RELATED` is system managed — the portal never creates one, it only reads them back.
 */
export enum ShareClass {
  EXPLICIT = 'EXPLICIT',
  TASK_RELATED = 'TASK_RELATED',
  DELEGATION = 'DELEGATION',
}

/**
 * The share classes a user can choose in the first step of the share flow. `TASK_RELATED` is
 * excluded because the API only accepts `EXPLICIT` or `DELEGATION` on create.
 */
export type SelectableShareClass = ShareClass.EXPLICIT | ShareClass.DELEGATION
